import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRestaurants } from '../../hooks/useRestaurants';
import { api } from '../../utils/apiClient';
import type { Product } from '../../types';
import CustomizationModal from '../../components/CustomizationModal';
import './CreateOrderModal.css';

interface CreateOrderItem {
  tempId: string;
  productId: string;
  quantity: number;
  customization?: Record<string, string | string[]>;
}

interface CreateOrderModalProps {
  restaurantId?: string;
  onClose: () => void;
  onSuccess: (orderData?: any) => void;
}

function CreateOrderModal({ restaurantId: initialRestaurantId, onClose, onSuccess }: CreateOrderModalProps) {
  const { t } = useTranslation();
  const { restaurants, loading: loadingRestaurants, error: restaurantsError, fetchRestaurants } = useRestaurants();
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(initialRestaurantId || '');
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedItems, setSelectedItems] = useState<CreateOrderItem[]>([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customizeItem, setCustomizeItem] = useState<{ product: Product; item: CreateOrderItem } | null>(null);

  useEffect(() => {
    fetchRestaurants().then((filtered) => {
      if (initialRestaurantId) setSelectedRestaurantId(initialRestaurantId);
      else if (filtered.length > 0) setSelectedRestaurantId(filtered[0].id);
    });
  }, [fetchRestaurants, initialRestaurantId]);

  const fetchProducts = useCallback(async () => {
    if (!selectedRestaurantId) return;
    try {
      const { data } = await api.get<Product[]>(`/api/products/restaurant/${selectedRestaurantId}`);
      setProducts(data.filter((p) => p.isActive !== false));
      setError('');
    } catch (err: any) {
      setError('modals.failedLoadProducts');
    }
  }, [selectedRestaurantId]);

  useEffect(() => {
    if (selectedRestaurantId) fetchProducts();
    else {
      setProducts([]);
      setSelectedItems([]);
    }
  }, [selectedRestaurantId, fetchProducts]);

  const addItem = (productId: string, openCustomize = false) => {
    const product = products.find(p => p.id === productId);
    const isEditable = product?.isEditable && product?.optionGroups?.length;
    const newItem: CreateOrderItem = {
      tempId: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      productId,
      quantity: 1,
      customization: undefined,
    };
    setSelectedItems(prev => [...prev, newItem]);
    if (openCustomize && isEditable) {
      setCustomizeItem({ product: product!, item: newItem });
    }
  };

  const removeItem = (tempId: string) => {
    setSelectedItems(prev => prev.filter(item => item.tempId !== tempId));
  };

  const changeQty = (tempId: string, delta: number) => {
    setSelectedItems(prev => prev.map(item => {
      if (item.tempId !== tempId) return item;
      const newQty = item.quantity + delta;
      if (newQty <= 0) return null;
      return { ...item, quantity: newQty };
    }).filter((item): item is CreateOrderItem => item !== null));
  };

  const updateItemCustomization = (tempId: string, customization: Record<string, string | string[]> | undefined) => {
    setSelectedItems(prev => prev.map(item =>
      item.tempId === tempId ? { ...item, customization } : item
    ));
  };

  const getItemsForProduct = (productId: string) =>
    selectedItems.filter(item => item.productId === productId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!selectedRestaurantId) {
      setError('modals.pleaseSelectRestaurant');
      setLoading(false);
      return;
    }

    if (selectedItems.length === 0) {
      setError('modals.pleaseSelectItem');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/orders', {
        restaurantId: selectedRestaurantId,
        items: selectedItems.map(({ productId, quantity, customization }) => ({ productId, quantity, customization })),
        notes: notes || undefined,
      });
      onSuccess(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'modals.failedCreateOrder');
    } finally {
      setLoading(false);
    }
  };

  const getItemPrice = (product: Product, item: CreateOrderItem) => {
    const groups = product.optionGroups;
    const cust = item.customization;
    if (!groups?.length || !cust) return product.price;
    let p = product.price;
    for (const g of groups) {
      const sel = cust[g.label];
      if (!sel) continue;
      const vals = Array.isArray(sel) ? sel : [sel];
      for (const v of vals) {
        const c = g.choices.find((x) => x.label === v);
        if (c?.priceModifier != null) p += Number(c.priceModifier);
      }
    }
    return p;
  };

  const formatCustomization = (cust: Record<string, string | string[]> | undefined): string => {
    if (!cust || Object.keys(cust).length === 0) return '';
    return Object.entries(cust)
      .map(([, v]) => (Array.isArray(v) ? v.join(', ') : v))
      .filter(Boolean)
      .join(' • ');
  };

  const getTotal = () => {
    return selectedItems.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      return sum + (product ? getItemPrice(product, item) * item.quantity : 0);
    }, 0);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('modals.createOrder')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {(error || restaurantsError) && <div className="error-box">{(error || restaurantsError).startsWith('modals.') ? t(error || restaurantsError) : (error || restaurantsError)}</div>}

            <div className="form-group">
              <label>{t('modals.restaurant')} *</label>
              <select
                value={selectedRestaurantId}
                onChange={(e) => setSelectedRestaurantId(e.target.value)}
                required
                disabled={loadingRestaurants}
                className="restaurant-select"
              >
                <option value="">{t('modals.selectRestaurant')}</option>
                {restaurants.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name} {r._count && `(${r._count.products} products)`}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('modals.selectProducts')}</label>
              <div className="products-grid">
                {products.map(product => {
                  const items = getItemsForProduct(product.id);
                  const isEditable = product.isEditable && product.optionGroups?.length;

                  return (
                    <div key={product.id} className="product-card">
                      <div className="product-info">
                        <div className="product-name">{product.name}</div>
                        <div className="product-price">₸{product.price.toFixed(2)}</div>
                        <div className="product-category">{product.category}</div>
                      </div>
                      {items.length > 0 ? (
                        <div className="create-order-variants">
                          {items.map((item) => (
                            <div key={item.tempId} className="create-order-variant-row">
                              <div className="create-order-variant-info">
                                <span className="create-order-variant-custom">
                                  {formatCustomization(item.customization) || (isEditable ? t('orderStatus.default') : '')}
                                </span>
                                <span className="create-order-variant-price">₸{getItemPrice(product, item).toFixed(2)}</span>
                              </div>
                              <div className="create-order-variant-controls">
                                <div className="quantity-controls">
                                  <button type="button" onClick={() => changeQty(item.tempId, -1)} className="qty-btn">−</button>
                                  <span className="qty-value">{item.quantity}</span>
                                  <button type="button" onClick={() => changeQty(item.tempId, 1)} className="qty-btn">+</button>
                                </div>
                                {isEditable && (
                                  <button type="button" className="create-order-edit-btn" onClick={() => setCustomizeItem({ product, item })}>
                                    {t('orderStatus.edit')}
                                  </button>
                                )}
                                <button type="button" className="create-order-remove-btn" onClick={() => removeItem(item.tempId)} title={t('orderStatus.remove')}>×</button>
                              </div>
                            </div>
                          ))}
                          {isEditable && (
                            <button
                              type="button"
                              className="create-order-add-another-btn"
                              onClick={() => addItem(product.id, true)}
                            >
                              + {t('orderStatus.addAnotherVariant')}
                            </button>
                          )}
                        </div>
                      ) : (
                        <button type="button" onClick={() => addItem(product.id, !!isEditable)} className="add-btn">
                          {t('modals.add')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {selectedItems.length > 0 && (
              <div className="selected-items">
                <h3>{t('modals.selectedItems')}</h3>
                {selectedItems.map((item) => {
                  const product = products.find(p => p.id === item.productId);
                  if (!product) return null;
                  return (
                    <div key={item.tempId} className="selected-item">
                      <span>
                        {product.name} {formatCustomization(item.customization) && `(${formatCustomization(item.customization)})`} x{item.quantity}
                      </span>
                      <span>₸{(getItemPrice(product, item) * item.quantity).toFixed(2)}</span>
                    </div>
                  );
                })}
                <div className="selected-total">
                  <strong>{t('modals.total')}: ₸{getTotal().toFixed(2)}</strong>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>{t('modals.notesOptional')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('modals.notesPlaceholder')}
                rows={3}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-cancel">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading || selectedItems.length === 0} className="btn-submit">
              {loading ? t('modals.creating') : t('modals.createOrderBtn')}
            </button>
          </div>
        </form>

        {customizeItem && customizeItem.product.optionGroups?.length && (
          <CustomizationModal
            productName={customizeItem.product.name}
            basePrice={customizeItem.product.price}
            optionGroups={customizeItem.product.optionGroups}
            initialCustomization={customizeItem.item.customization || null}
            onSave={(cust) => {
              updateItemCustomization(customizeItem.item.tempId, cust);
              setCustomizeItem(null);
            }}
            onClose={() => setCustomizeItem(null)}
          />
        )}
      </div>
    </div>
  );
}

export default CreateOrderModal;
